import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { filter, findIndex, some, split, toUpper, isEmpty, isNil, trim, toLower, without } from "lodash";
import { ShowMore } from "@re-dev/react-truncate";
import { Tooltip } from "react-tooltip";
import { useAuth0 } from "@auth0/auth0-react";
import { clsx } from "clsx";
import { useQuery } from "@tanstack/react-query";
import { isMobile } from "react-device-detect";

import LeafletMap from "@/components/common/LeafletMap";
import PropertyImageSwiper from "@/components/common/property-image-swiper/property-image-swiper";
import SalesInfo from "@/pages/daily-properties/sales-info/sales-info";
import SimilarProperties from "@/pages/daily-properties/similar-properties/similar-properties";
import AgentCard from "@/components/common/AgentCard";
import { getSalesData } from "@/services/PropertyService";
import AdSenseAd from "@/components/common/AdSenseAd/AdSenseAd";
import config from "@/config";
import {
    attachFullscreenFancyboxFooterAd,
    computeShouldRenderFullscreenStickyAd,
    FULLSCREEN_STICKY_FOOTER_BODY_CLASS,
    getFullscreenSafeAreaInsetBottomPx,
    removeFullscreenFancyboxFooterAd,
} from "@/ads/fullscreen-sticky-footer-layout";
import type { PuzzleListingView } from "@/helpers/puzzle-view-url";

import { PROPERTY_TYPE } from "@/lang/property-type.lang";
import { TOOLTIP_TEXT } from "@/lang/tooltip-text.lang";
import { isZero } from "@/helpers/helpers";
import useLocalStorage from "@/hooks/useLocalStorage";
import { AutoTextSize } from "auto-text-size";

import "./property-info.scss";
import CustomButton from "@/components/common/buttons/custom-button/custom-button";
import { RiExternalLinkLine } from "react-icons/ri";
import V3FlipSwiper from "@/pages/LiveVariant/components/Flip/V3FlipSwiper";
import { X } from "react-bootstrap-icons";
import { IoIosArrowBack } from "react-icons/io";
import { initializeFancybox } from "@/helpers/initializeFancybox";
import { UUID } from "crypto";

interface PropertyInfoData {
    id: UUID;
    streetAddress?: string;
    neighborhood: string;
    state: string;
    propertyType?: string;
    bedrooms: number;
    bathrooms: number;
    squareFootage?: number;
    publicRemarks?: string;
    imageUrls?: string[];
    lotDimensions?: string;
    laundryFeatures?: string;
    heating?: string;
    cooling?: string;
    statusIndicator?: string;
    parkingSpots?: number;
    parkingType?: string;
    yearBuilt?: number;
    exteriorFeatures?: string;
    interiorFeatures?: string;
    mlsId?: string | number;
    // snake_case properties (for INIT_PROPERTY_VALUES compatibility)
    street_address?: string;
    image_urls?: string[];
    listingAgent?: AgentData | null;
}

interface AgentData {
    id?: string | null;
    userId?: string | null;
    name: string;
    company: string;
    profilePicture: string | null;
    profileUrl: string | null;
    nickname?: string | null;
    isFollowing?: boolean;
    cta: string | null;
    mlsId: string | null;
}

interface PropertyInfoProps {
    property: PropertyInfoData;
    arrProperties?: PropertyInfoData[];
    showMoreDetails?: boolean;
    round?: number;
    unBlurAll?: boolean;
    index?: number;
    showFullListing?: () => void;
    onBackClick?: () => void;
    showBanner?: boolean;
    bannerStep?: 1 | 2;
    /** Optional overlay on the image slider (e.g. puzzle property like control). */
    imageSliderOverlay?: React.ReactNode;
    /** Optional centered overlay on the image slider (e.g. puzzle Listing / Scoreboard toggle). */
    imageSliderTopOverlay?: React.ReactNode;
    /** Current puzzle `?view=` when rendered on a puzzle listing (from parent). */
    puzzleView?: PuzzleListingView;
    /** Updates puzzle `?view=` (from parent `usePuzzleViewUrl`). */
    onPuzzleViewChange?: (view: PuzzleListingView) => void;
    /** View to restore when closing photo UI (listing vs scoreboard tab). */
    puzzleMediaRestoreView?: PuzzleListingView;
}

/**
 * Component for displaying property information.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.property - The property data.
 * @param {Array} props.arrProperties - The array of properties.
 * @returns {JSX.Element} Property information component.
 */
const PropertyInfo: React.FC<PropertyInfoProps> = ({
    property,
    arrProperties,
    showMoreDetails = true,
    round,
    unBlurAll,
    index,
    showFullListing,
    onBackClick,
    showBanner = false,
    bannerStep = 1,
    imageSliderOverlay,
    imageSliderTopOverlay,
    puzzleView,
    onPuzzleViewChange,
    puzzleMediaRestoreView = "listing",
}) => {
    const syncPuzzleView = Boolean(onPuzzleViewChange);
    const [showMore, setShowMore] = useState(false);
    const [preferredVersion] = useLocalStorage("live-variant-preferred-version", "classic");
    const usePuzzleListingHero =
        preferredVersion === "puzzle" && Boolean(imageSliderTopOverlay || imageSliderOverlay || onBackClick);
    // For puzzle version, exclude block 6 (SimilarProperties) from blurredBlocks since it's hidden
    const [blurredBlocks, setBlurredBlocks] = useState(
        preferredVersion === "puzzle" ? [2, 3, 4, 5, 7, 8] : [2, 3, 4, 5, 6, 7, 8],
    ); // block 1 is always revealed initially
    const [hasUnblurredTheRound, setHasUnblurredTheRound] = useState(true);
    const { isAuthenticated } = useAuth0();
    // Handle both camelCase and snake_case property formats
    const streetAddress = property.streetAddress ?? property.street_address ?? "";
    const imageUrls = useMemo<string[]>(
        () => property.imageUrls ?? property.image_urls ?? [],
        [property.imageUrls, property.image_urls],
    );
    const statusIndicator = property.statusIndicator;
    const propertyTypeRaw = property.propertyType ?? "";
    const squareFootage = property.squareFootage ?? 0;
    const publicRemarks = property.publicRemarks ?? "";
    const parkingSpots = property.parkingSpots ?? 0;
    const parkingType = property.parkingType ?? "";
    const yearBuilt = property.yearBuilt ?? 0;
    const exteriorFeatures = property.exteriorFeatures ?? "";
    const interiorFeatures = property.interiorFeatures ?? "";
    const laundryFeatures = property.laundryFeatures ?? "";
    const lotDimensions = property.lotDimensions ?? "";

    const propertyAddress = split(streetAddress, ",")[0];
    const isPlusMinus = !isNil(round);
    const isV3Flip = preferredVersion === "flip";
    const propertyType = (PROPERTY_TYPE as any)[propertyTypeRaw] || propertyTypeRaw;
    const [isVerticalModalOpenLocal, setIsVerticalModalOpenLocal] = useState(false);
    /** Optimistic open/close while `?view=` catches up; cleared once URL matches. */
    const [verticalModalSyncOverride, setVerticalModalSyncOverride] = useState<boolean | null>(null);
    const verticalModalRef = useRef<HTMLDivElement>(null);
    const [agentData, setAgentData] = useState<AgentData | null>(null);
    const [isVerticalModalAdMounted, setIsVerticalModalAdMounted] = useState<boolean>(false);
    const [isFullscreenViewerOpen, setIsFullscreenViewerOpen] = useState<boolean>(false);
    const [fullscreenSlideIndex, setFullscreenSlideIndex] = useState<number>(0);
    const [fullscreenImageAspectRatio, setFullscreenImageAspectRatio] = useState<number | null>(null);
    const [fullscreenViewportSize, setFullscreenViewportSize] = useState<{ width: number; height: number }>(() => ({
        width: typeof window !== "undefined" ? window.innerWidth : 0,
        height: typeof window !== "undefined" ? window.innerHeight : 0,
    }));
    const [fullscreenSafeAreaBottomInsetPx, setFullscreenSafeAreaBottomInsetPx] = useState<number>(0);

    const { data: salesData } = useQuery({
        queryKey: ["current_property_sales_data", property.id],
        queryFn: () => getSalesData(property.id),
        enabled: !isNil(property.id) && !isEmpty(property.id),
        gcTime: 5 * 60 * 1000, // 5 minutes
    });

    useEffect(() => {
        const listingAgentDaily = (property as any)?.listingAgent;
        const listingAgentSales = (salesData as any)?.sales_data?.listingAgent;
        if (listingAgentSales) {
            const nickname: string | null = listingAgentSales?.nickname ?? null;
            const displayName = String(listingAgentSales?.displayName ?? "").trim();
            const fullName = `${listingAgentSales?.firstName ?? ""} ${listingAgentSales?.lastName ?? ""}`.trim();

            const transformedAgentData: AgentData = {
                id: listingAgentSales?.id ? String(listingAgentSales.id) : null,
                userId: listingAgentSales?.userId ? String(listingAgentSales.userId) : null,
                name: displayName || fullName || "Listing Agent",
                company: listingAgentSales?.brokerage || "",
                profilePicture:
                    listingAgentSales?.profilePhotoUrl ||
                    listingAgentSales?.img ||
                    null,
                profileUrl: listingAgentSales?.link || null,
                isFollowing:
                    typeof listingAgentDaily?.isFollowing === "boolean"
                        ? listingAgentDaily.isFollowing
                        : undefined,
                cta: listingAgentSales?.cta || null,
                mlsId: listingAgentSales?.mlsId || null,
                nickname,
            };
            setAgentData(transformedAgentData);
        } else {
            setAgentData(null);
        }
    }, [property, salesData]);

    const mediaRestoreView: PuzzleListingView =
        puzzleMediaRestoreView === "scoreboard" ? "scoreboard" : "listing";

    const isVerticalModalOpen = useMemo((): boolean => {
        if (isFullscreenViewerOpen) {
            return false;
        }
        if (syncPuzzleView) {
            if (verticalModalSyncOverride !== null) {
                return verticalModalSyncOverride;
            }
            return puzzleView === "vertical";
        }
        return isVerticalModalOpenLocal;
    }, [
        isFullscreenViewerOpen,
        isVerticalModalOpenLocal,
        puzzleView,
        syncPuzzleView,
        verticalModalSyncOverride,
    ]);

    const openVerticalPhotoScroll = useCallback(() => {
        if (syncPuzzleView && onPuzzleViewChange) {
            setVerticalModalSyncOverride(true);
            onPuzzleViewChange("vertical");
            return;
        }
        setIsVerticalModalOpenLocal(true);
    }, [onPuzzleViewChange, syncPuzzleView]);

    const closeVerticalPhotoScroll = useCallback(() => {
        if (syncPuzzleView && onPuzzleViewChange) {
            setVerticalModalSyncOverride(false);
            onPuzzleViewChange(mediaRestoreView);
            return;
        }
        setIsVerticalModalOpenLocal(false);
    }, [mediaRestoreView, onPuzzleViewChange, syncPuzzleView]);

    // Release optimistic override once `?view=` matches (deep links / browser back use URL only).
    useEffect(() => {
        if (!syncPuzzleView || verticalModalSyncOverride === null) {
            return;
        }
        const urlWantsVerticalOpen = puzzleView === "vertical";
        if (verticalModalSyncOverride === urlWantsVerticalOpen) {
            setVerticalModalSyncOverride(null);
        }
    }, [puzzleView, syncPuzzleView, verticalModalSyncOverride]);

    // Mount modal ad anchors after the modal settles to avoid rendering jank on open.
    useEffect(() => {
        if (!isVerticalModalOpen) {
            setIsVerticalModalAdMounted(false);
            return;
        }
        const timeoutId = window.setTimeout(() => {
            setIsVerticalModalAdMounted(true);
        }, 500);
        return () => window.clearTimeout(timeoutId);
    }, [isVerticalModalOpen]);

    useEffect(() => {
        const selectedImage = imageUrls[fullscreenSlideIndex];
        if (!isFullscreenViewerOpen || !selectedImage) {
            setFullscreenImageAspectRatio(null);
            return;
        }
        let isCancelled = false;
        const image = new Image();
        image.onload = () => {
            if (isCancelled) {
                return;
            }
            if (!image.naturalWidth || !image.naturalHeight) {
                setFullscreenImageAspectRatio(null);
                return;
            }
            setFullscreenImageAspectRatio(image.naturalWidth / image.naturalHeight);
        };
        image.onerror = () => {
            if (isCancelled) {
                return;
            }
            setFullscreenImageAspectRatio(null);
        };
        image.src = selectedImage;
        return () => {
            isCancelled = true;
            image.onload = null;
            image.onerror = null;
        };
    }, [fullscreenSlideIndex, imageUrls, isFullscreenViewerOpen]);

    useEffect(() => {
        if (!isFullscreenViewerOpen) {
            return;
        }
        const handleViewportResize = () => {
            setFullscreenViewportSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
            setFullscreenSafeAreaBottomInsetPx(getFullscreenSafeAreaInsetBottomPx());
        };
        handleViewportResize();
        window.addEventListener("resize", handleViewportResize);
        window.addEventListener("orientationchange", handleViewportResize);
        return () => {
            window.removeEventListener("resize", handleViewportResize);
            window.removeEventListener("orientationchange", handleViewportResize);
        };
    }, [isFullscreenViewerOpen]);

    useEffect(() => {
        if (typeof document === "undefined") {
            return;
        }
        if (!isFullscreenViewerOpen) {
            document.body.classList.remove(FULLSCREEN_STICKY_FOOTER_BODY_CLASS);
            removeFullscreenFancyboxFooterAd();
            return;
        }
        document.body.classList.add(FULLSCREEN_STICKY_FOOTER_BODY_CLASS);
        return () => {
            document.body.classList.remove(FULLSCREEN_STICKY_FOOTER_BODY_CLASS);
            removeFullscreenFancyboxFooterAd();
        };
    }, [isFullscreenViewerOpen]);

    const shouldRenderFullscreenStickyAd = useMemo(
        () =>
            computeShouldRenderFullscreenStickyAd({
                isFullscreenOpen: isFullscreenViewerOpen,
                viewportWidth: fullscreenViewportSize.width,
                viewportHeight: fullscreenViewportSize.height,
                imageAspectRatio: fullscreenImageAspectRatio,
                safeAreaBottomInsetPx: fullscreenSafeAreaBottomInsetPx,
            }),
        [
            fullscreenImageAspectRatio,
            fullscreenSafeAreaBottomInsetPx,
            fullscreenViewportSize.height,
            fullscreenViewportSize.width,
            isFullscreenViewerOpen,
        ],
    );

    useEffect(() => {
        if (!isFullscreenViewerOpen || !shouldRenderFullscreenStickyAd) {
            removeFullscreenFancyboxFooterAd();
            return;
        }
        removeFullscreenFancyboxFooterAd();
        const frameId = window.requestAnimationFrame(() => {
            attachFullscreenFancyboxFooterAd(null);
        });
        return () => {
            window.cancelAnimationFrame(frameId);
            removeFullscreenFancyboxFooterAd();
        };
    }, [isFullscreenViewerOpen, shouldRenderFullscreenStickyAd, fullscreenSafeAreaBottomInsetPx]);

    /**
     * Function to check and update property address if duplicates are found
     *
     * @param {Object} property - The property data.
     * @param {Array} arrProperties - The array of properties.
     * @returns {string} The unique property address.
     */
    const makeAddressUnique = (property: PropertyInfoData, arrProperties?: PropertyInfoData[]): string => {
        const normalizedStreetAddress = toLower(trim(propertyAddress));
        const normalizedNeighborhood = toLower(trim(property.neighborhood));

        if (!arrProperties) {
            return propertyAddress;
        }

        const otherProperties = filter(arrProperties, (p) => p.id !== property.id);
        const hasDuplicate = some(
            otherProperties,
            (p) =>
                split(toLower(trim(p.streetAddress ?? "")), ",")[0] === normalizedStreetAddress &&
                toLower(trim(p.neighborhood)) === normalizedNeighborhood,
        );

        if (hasDuplicate) {
            const currentPropertyIndex = findIndex(arrProperties, { id: property.id });
            return `#${currentPropertyIndex + 1} ${propertyAddress}`;
        }

        return propertyAddress;
    };

    const uniquePropertyAddress = makeAddressUnique(property, arrProperties);

    const isCommonLotDimension =
        !isNil(lotDimensions) && !isEmpty(lotDimensions) && toUpper(lotDimensions) === "COMMON";

    const isUnknownLaundryFeature =
        !isNil(laundryFeatures) && !isEmpty(laundryFeatures) && toUpper(laundryFeatures) === "UNKNOWN";

    const handleRevealInfo = (block: number) => {
        const expectedUnblurredBlocks = round! - 1; // How many should have been unblurred
        // Calculate total revealable blocks: 7 for classic/flip, 6 for puzzle (excludes block 6)
        const totalRevealableBlocks = preferredVersion === "puzzle" ? 6 : 7;
        const actualUnblurredBlocks = totalRevealableBlocks - blurredBlocks.length; // How many are actually unblurred

        // Check if user has already unblurred info this round
        if ((!hasUnblurredTheRound || expectedUnblurredBlocks > actualUnblurredBlocks) && isPlusMinus && round !== 1) {
            setBlurredBlocks((prev) => without(prev, block));
            setHasUnblurredTheRound(true); // Mark that info has been unblurred for this round
        }
    };

    // Track round changes and reset `hasUnblurredTheRound` when a new round starts
    useEffect(() => {
        if (round && round > 1) {
            setHasUnblurredTheRound(false);
        }
    }, [round]);

    useEffect(() => {
        if (unBlurAll) {
            setBlurredBlocks([]);
        }
    }, [unBlurAll]);

    return (
        <>
            {/* Vertical Modal for Images */}
            {isVerticalModalOpen && (
                <div
                    ref={verticalModalRef}
                    className="property-info-vertical-scroll priceme-page-with-sticky-footer tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-black/90 tw-z-[9999] tw-overflow-y-auto"
                >
                    <button
                        className="tw-fixed tw-top-[1.5svh] tw-right-6 tw-text-white tw-text-2xl tw-font-bold tw-z-[100] tw-bg-black/50 tw-rounded-full"
                        onClick={closeVerticalPhotoScroll}
                        aria-label="Close modal"
                    >
                        <X />
                    </button>
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-[2px] tw-w-full tw-mx-auto tw-max-w-[728px]">
                        {imageUrls.map((image, idx) => (
                            <React.Fragment key={idx}>
                                <a
                                    href={image}
                                    className="tw-w-full"
                                    style={{ cursor: "pointer" }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (syncPuzzleView && onPuzzleViewChange) {
                                            setVerticalModalSyncOverride(false);
                                            onPuzzleViewChange("full_screen");
                                        } else {
                                            setIsVerticalModalOpenLocal(false);
                                        }
                                        const options = {
                                            startIndex: idx,
                                            Toolbar: { display: { right: ["close"] } },
                                            Thumbs: false,
                                            on: {
                                                "Carousel.ready": (fancybox: any) => {
                                                    setIsFullscreenViewerOpen(true);
                                                    setFullscreenSlideIndex(fancybox.getSlide().index);
                                                },
                                                "Carousel.change": (fancybox: any) => {
                                                    setFullscreenSlideIndex(fancybox.getSlide().index);
                                                },
                                                close: () => {
                                                    setIsFullscreenViewerOpen(false);
                                                    setFullscreenImageAspectRatio(null);
                                                    removeFullscreenFancyboxFooterAd();
                                                    if (syncPuzzleView && onPuzzleViewChange) {
                                                        setVerticalModalSyncOverride(true);
                                                        onPuzzleViewChange("vertical");
                                                    } else {
                                                        setIsVerticalModalOpenLocal(true);
                                                    }
                                                },
                                            },
                                        };
                                        initializeFancybox(imageUrls, options);
                                    }}
                                >
                                    <img
                                        src={image}
                                        alt={`PropertySwiper ${idx + 1}`}
                                        className="tw-w-full md:tw-max-h-screen tw-object-contain tw-rounded"
                                    />
                                </a>
                                {/* Insert ad every 5th photo after delayed modal mount */}
                                {isVerticalModalAdMounted && (idx + 1) % 5 === 0 && (
                                    <div className="tw-w-full">
                                        <AdSenseAd
                                            slot={config.ADSENSE_PROPERTY_PHOTOS_SLOT_ID}
                                            format="fluid"
                                        />
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    <AdSenseAd placementType="sticky-footer" />
                </div>
            )}
            <div className="position-relative">
                {usePuzzleListingHero ? (
                    <div className="puzzle-listing-hero tw-relative">
                        {imageSliderTopOverlay}
                        {onBackClick && (
                            <button
                                type="button"
                                className="tw-absolute tw-left-3 tw-top-3 tw-z-[50] tw-bg-black/70 tw-backdrop-blur-md tw-text-white tw-rounded-full tw-shadow-[0_2px_10px_rgba(0,0,0,0.45)] tw-ring-1 tw-ring-black/25 tw-transition-all tw-flex tw-items-center tw-justify-center tw-p-2"
                                onClick={onBackClick}
                                aria-label="Go back"
                            >
                                <IoIosArrowBack className="tw-h-6 tw-w-6 tw-text-white tw-cursor-pointer" />
                            </button>
                        )}
                        {imageSliderOverlay && (
                            <div className="puzzle-listing-like-overlay tw-absolute tw-z-[50] tw-flex tw-items-center tw-justify-center tw-pointer-events-none [&>*]:tw-pointer-events-auto">
                                {imageSliderOverlay}
                            </div>
                        )}
                        {isV3Flip ? (
                            <V3FlipSwiper property={property} />
                        ) : (
                            <PropertyImageSwiper
                                property={property}
                                round={round}
                                blurredBlocks={blurredBlocks}
                                handleRevealInfo={handleRevealInfo}
                                hasUnblurredTheRound={hasUnblurredTheRound}
                                index={index}
                                hasBackButton={Boolean(onBackClick)}
                                onImageClick={() => {
                                    openVerticalPhotoScroll();
                                }}
                            />
                        )}
                    </div>
                ) : (
                    <>
                        {onBackClick && (
                            <button
                                type="button"
                                className={clsx(
                                    "tw-fixed tw-z-[50] tw-bg-black/50 tw-backdrop-blur-md tw-text-white tw-rounded-full tw-shadow-md tw-transition-all tw-flex tw-items-center tw-justify-center tw-p-2",
                                    isMobile ? "tw-left-2" : "tw-left-[30rem]",
                                    isMobile && showBanner
                                        ? bannerStep === 1
                                            ? "tw-top-[20svh]"
                                            : "tw-top-[48svh]"
                                        : "tw-top-[10svh]",
                                )}
                                onClick={onBackClick}
                                aria-label="Go back"
                            >
                                <IoIosArrowBack className="tw-h-6 tw-w-6 md:tw-h-10 md:tw-w-10 lg:tw-h-7 lg:tw-w-7 tw-text-white tw-cursor-pointer" />
                            </button>
                        )}
                        {imageSliderOverlay && (
                            <div
                                className={clsx(
                                    "tw-fixed tw-z-[50]",
                                    isMobile ? "tw-right-2" : "tw-right-[2rem]",
                                    isMobile && showBanner
                                        ? bannerStep === 1
                                            ? "tw-top-[20svh]"
                                            : "tw-top-[48svh]"
                                        : "tw-top-[10svh]",
                                )}
                            >
                                {imageSliderOverlay}
                            </div>
                        )}
                        {isV3Flip ? (
                            <V3FlipSwiper property={property} />
                        ) : (
                            <PropertyImageSwiper
                                property={property}
                                round={round}
                                blurredBlocks={blurredBlocks}
                                handleRevealInfo={handleRevealInfo}
                                hasUnblurredTheRound={hasUnblurredTheRound}
                                index={index}
                                hasBackButton={Boolean(onBackClick)}
                                onImageClick={() => {
                                    openVerticalPhotoScroll();
                                }}
                            />
                        )}
                    </>
                )}

                <div
                    className={clsx("property-details property-name tw-flex tw-flex-col tw-px-2", {
                        "!tw-py-0": isV3Flip,
                    })}
                >
                    <div className={clsx("property-details-key", { "tw-h-0": isV3Flip })}>
                        <div
                            className={clsx({
                                "property-details-blur": isPlusMinus && blurredBlocks.includes(4),
                                "tw-px-2": isV3Flip,
                            })}
                            onClick={() => handleRevealInfo(4)}
                        >
                            {!isV3Flip && (
                                <h2 id="address">
                                    <>
                                        {uniquePropertyAddress} |{" "}
                                        {preferredVersion === "puzzle"
                                            ? `${property.neighborhood}, ${property.state}`
                                            : property.neighborhood}
                                    </>
                                </h2>
                            )}

                            {showFullListing && (
                                <div className="tw-flex tw-justify-center">
                                    <CustomButton
                                        id="see-full-listing"
                                        dataCy="see-full-listing"
                                        className="tw-h-[26px] tw-flex tw-gap-1 tw-justify-center tw-items-center tw-text-[13px] tw-bg-[#FAB85F] tw-border-[1px] tw-border-solid tw-border-[#F69929] tw-rounded-[500px] tw-text-black"
                                        text="See Full Listing"
                                        rightIcon={<RiExternalLinkLine fontSize={15} />}
                                        handleClick={showFullListing}
                                    />
                                </div>
                            )}
                        </div>

                        <div
                            className={clsx(
                                "main-info-row px-0",
                                { "property-details-blur": isPlusMinus && blurredBlocks.includes(5) },
                                {
                                    "tw-relative tw-z-[1] tw-bottom-[72px]  md:tw tw-text-white tw-mb-[-70px] tw-bg-[#2E2E2E] tw-bg-opacity-[0.3]":
                                        isV3Flip,
                                },
                            )}
                            onClick={() => handleRevealInfo(5)}
                        >
                            {isV3Flip ? (
                                <>
                                    <div
                                        className={clsx("main-info-column bed-column", {
                                            "!tw-flex-row": isV3Flip,
                                        })}
                                    >
                                        <i className="tw-p-2 fa-solid fa-bed"></i>
                                        <span
                                            id="bedrooms"
                                            className={clsx("tw-pb-2", {
                                                "!tw-text-white !tw-pb-0 !tw-text-lg md:!tw-text-xl": isV3Flip,
                                            })}
                                        >
                                            {property.bedrooms}
                                        </span>
                                    </div>
                                    <div
                                        className={clsx("main-info-column bath-column", {
                                            "!tw-flex-row": isV3Flip,
                                        })}
                                    >
                                        <i className="tw-p-2 fa-solid fa-bath"></i>
                                        <span
                                            id="bathrooms"
                                            className={clsx({
                                                "!tw-text-white !tw-text-lg md:!tw-text-xl": isV3Flip,
                                            })}
                                        >
                                            {property.bathrooms}
                                        </span>
                                    </div>
                                    <div
                                        className={clsx("main-info-column bed-column", {
                                            "!tw-flex-row": isV3Flip,
                                        })}
                                    >
                                        <i className="tw-p-2 fa-solid fa-maximize"></i>
                                        <span
                                            className={clsx(!isZero(squareFootage) && "has-tooltip", {
                                                "!tw-text-white !tw-text-lg md:!tw-text-xl": isV3Flip,
                                            })}
                                            id="square-footage"
                                            data-tooltip-id="square-footage"
                                        >
                                            {isZero(squareFootage) ? "Unknown" : squareFootage}{" "}
                                            {!isZero(squareFootage) && "Sq.ft."}
                                        </span>
                                    </div>{" "}
                                    <div
                                        className={clsx("main-info-column type-column", {
                                            "!tw-flex-row": isV3Flip,
                                        })}
                                    >
                                        <i className="tw-p-2 fa-solid fa-house-circle-exclamation"></i>
                                        <span
                                            id="property-type"
                                            className={clsx({
                                                "!tw-text-white !tw-text-lg md:!tw-text-xl": isV3Flip,
                                            })}
                                        >
                                            {isV3Flip
                                                ? propertyType.toLowerCase().includes("single family")
                                                    ? "House"
                                                    : propertyType
                                                : propertyType}
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="main-info-column bed-column">
                                        <p>Beds</p>
                                        <span
                                            id="bedrooms"
                                            className={clsx("tw-pb-2", {
                                                "!tw-text-white !tw-pb-0 !tw-text-lg md:!tw-text-xl": isV3Flip,
                                            })}
                                        >
                                            {property.bedrooms}
                                        </span>
                                    </div>
                                    <div className="main-info-column bath-column">
                                        <p>Baths</p>
                                        <span
                                            id="bathrooms"
                                            className={clsx({
                                                "!tw-text-white !tw-text-lg md:!tw-text-xl": isV3Flip,
                                            })}
                                        >
                                            {property.bathrooms}
                                        </span>
                                    </div>
                                    <div className="main-info-column sq-column">
                                        <p>Sq. Ft</p>
                                        <span
                                            className={clsx(!isZero(squareFootage) && "has-tooltip", {
                                                "!tw-text-white !tw-text-lg md:!tw-text-xl": isV3Flip,
                                            })}
                                            id="square-footage"
                                            data-tooltip-id="square-footage"
                                        >
                                            {isZero(squareFootage) ? "Unknown" : squareFootage} {isV3Flip && "Sq.ft."}
                                        </span>
                                        {!isZero(squareFootage) && isPlusMinus && !blurredBlocks.includes(5) && (
                                            <Tooltip
                                                id="square-footage"
                                                anchorSelect="[data-tooltip-id='square-footage']"
                                                content={TOOLTIP_TEXT.SQUARE_FOOTAGE}
                                                place="top"
                                            />
                                        )}
                                    </div>
                                    <div className="main-info-column type-column">
                                        <p>Type</p>

                                        <span
                                            id="property-type"
                                            className={clsx({
                                                "!tw-text-white !tw-text-lg md:!tw-text-xl": isV3Flip,
                                            })}
                                        >
                                            {propertyType}
                                        </span>
                                    </div>
                                </>
                            )}
                            {isV3Flip && (
                                <h5
                                    id="address"
                                    className="tw-w-full tw-flex !tw-justify-center !tw-text-black tw-bg-white tw-px-1 md:tw-px-2 tw-py-2 md:tw-py-2.5"
                                >
                                    <AutoTextSize maxFontSizePx={18} className="!tw-text-center">
                                        {uniquePropertyAddress} | {property.neighborhood}, {property.state}
                                    </AutoTextSize>
                                </h5>
                            )}
                        </div>
                    </div>
                </div>

                <div
                    className={clsx("property-map-container tw-px-0", {
                        "property-details-blur": isPlusMinus && blurredBlocks.includes(4),
                        unclickable: isPlusMinus && hasUnblurredTheRound,
                    })}
                    onClick={() => handleRevealInfo(4)}
                >
                    <LeafletMap property={property} mapOverlayed={!showMoreDetails} />
                </div>

                {showMoreDetails ? (
                    <div className="property-details px-3">
                        <div className="property-details-key">
                            <div className="about-listing-row pb-3">
                                <div className="px-0">
                                    <h3 className="mt-2">
                                        <strong className="subtitle">About this listing</strong>
                                    </h3>
                                    {!showMore ? (
                                        <div
                                            className={clsx({
                                                "property-details-blur": isPlusMinus && blurredBlocks.includes(7),
                                                unclickable: isPlusMinus && hasUnblurredTheRound,
                                            })}
                                            onClick={() => handleRevealInfo(7)}
                                        >
                                            <ShowMore
                                                trimWhitespace
                                                className="truncated-public-remarks-container"
                                                lines={3}
                                                more={
                                                    <>
                                                        ...
                                                        <button
                                                            onClick={() => setShowMore(true)}
                                                            className="show-more-btn mt-2"
                                                        >
                                                            Show More
                                                        </button>
                                                    </>
                                                }
                                            >
                                                {publicRemarks}
                                            </ShowMore>
                                        </div>
                                    ) : (
                                        <p>
                                            <span className="public-remarks">{publicRemarks}</span>{" "}
                                            <span>
                                                <button
                                                    onClick={() => setShowMore(false)}
                                                    className="show-more-btn mt-2"
                                                >
                                                    Show less
                                                </button>
                                            </span>
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* AdSense Ad - Mobile only */}
                            {isMobile && (
                                <AdSenseAd
                                    slot={config.ADSENSE_PUZZLE_DETAILS_SLOT_ID}
                                    className="tw-my-4"
                                />
                            )}

                            {/** Temporarily hide similar properties for PUZZLE version **/}
                            {preferredVersion !== "puzzle" && (
                                <div
                                    className={clsx({
                                        "property-details-blur": isPlusMinus && blurredBlocks.includes(6),
                                        unclickable: isPlusMinus && hasUnblurredTheRound,
                                    })}
                                    onClick={() => handleRevealInfo(6)}
                                >
                                    <SimilarProperties property={property} isAuthenticated={isAuthenticated} />
                                </div>
                            )}

                            <div id="additional-details">
                                <div className="pt-3 pb-4" onClick={() => handleRevealInfo(8)}>
                                    <h3 className="mb-2">
                                        <strong className="subtitle">Additional Details</strong>
                                    </h3>
                                    <div className="details-item d-flex justify-content-between">
                                        <span>Parking Spots:</span>
                                        <p
                                            className={clsx({
                                                "property-details-blur": isPlusMinus && blurredBlocks.includes(8),
                                            })}
                                        >{`${parkingSpots} ${parkingType}`}</p>
                                    </div>
                                    <div className="details-item d-flex justify-content-between">
                                        <span>Year Built:</span>
                                        <p
                                            className={clsx({
                                                "property-details-blur": isPlusMinus && blurredBlocks.includes(8),
                                            })}
                                        >
                                            {isZero(yearBuilt) ? "Unknown" : yearBuilt}
                                        </p>
                                    </div>
                                    <div className="details-item d-flex justify-content-between">
                                        <span>Exterior Features:</span>
                                        <p
                                            className={clsx({
                                                "property-details-blur": isPlusMinus && blurredBlocks.includes(8),
                                            })}
                                        >
                                            {exteriorFeatures}
                                        </p>
                                    </div>
                                    <div className="details-item d-flex justify-content-between">
                                        <span>Interior Features:</span>
                                        <p
                                            className={clsx({
                                                "property-details-blur": isPlusMinus && blurredBlocks.includes(8),
                                            })}
                                        >
                                            {interiorFeatures}
                                        </p>
                                    </div>
                                    <div className="details-item d-flex justify-content-between">
                                        <span>Heating:</span>
                                        <p
                                            className={clsx({
                                                "property-details-blur": isPlusMinus && blurredBlocks.includes(8),
                                            })}
                                        >
                                            {property.heating}
                                        </p>
                                    </div>
                                    <div className="details-item d-flex justify-content-between">
                                        <span>AC:</span>
                                        <p
                                            className={clsx({
                                                "property-details-blur": isPlusMinus && blurredBlocks.includes(8),
                                            })}
                                        >
                                            {property.cooling}
                                        </p>
                                    </div>
                                    <div className="details-item d-flex justify-content-between">
                                        <span>Laundry Features:</span>
                                        <p
                                            className={clsx({
                                                "property-details-blur": isPlusMinus && blurredBlocks.includes(8),
                                            })}
                                        >
                                            <span
                                                className={clsx(isUnknownLaundryFeature && "has-tooltip")}
                                                data-tooltip-id="laundry-features"
                                            >
                                                {laundryFeatures}
                                            </span>
                                        </p>
                                        {isUnknownLaundryFeature && (
                                            <Tooltip
                                                id="laundry-features"
                                                anchorSelect="[data-tooltip-id='laundry-features']"
                                                content={TOOLTIP_TEXT.LAUNDRY_FEATURES}
                                                place="top"
                                            />
                                        )}
                                    </div>
                                    <div className="details-item d-flex justify-content-between">
                                        <span>Lot Dimensions:</span>
                                        <p
                                            className={clsx({
                                                "property-details-blur": isPlusMinus && blurredBlocks.includes(8),
                                            })}
                                        >
                                            <span
                                                className={clsx(isCommonLotDimension && "has-tooltip")}
                                                data-tooltip-id="lot-dimension"
                                            >
                                                {lotDimensions}
                                            </span>
                                        </p>
                                        {isCommonLotDimension && (
                                            <Tooltip
                                                id="lot-dimension"
                                                anchorSelect="[data-tooltip-id='lot-dimension']"
                                                content={TOOLTIP_TEXT.LOT_DIMENSIONS}
                                                place="top-start"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Mock agent data for demonstration */}
                            {agentData && (
                                <AgentCard
                                    agent={agentData as any}
                                    clickLocation="property_page"
                                />
                            )}

                            <SalesInfo property_id={property.id} propertyStatus={statusIndicator} />
                        </div>
                    </div>
                ) : null}
            </div>
        </>
    );
};

export default PropertyInfo;
